<template>
<v-tooltip top>
  <template v-slot:activator="{ on }">
    <v-autocomplete
      v-on="on"
      :value="value"
      :items="adGroupSelections"
      :item-text="item => isDC(item) ? item.party_name : item.name"
      :item-value="item => isDC(item) ? item.party_id : item.id"
      item-color="action"
      label="Additional Ad Groups*"
      clearable
      chips
      small-chips
      background-color="#fff"
      validate-on-blur
      dense
      multiple
      return-object
      outlined
      hide-details="auto"
      @change="handleChange">
      <template v-slot:prepend-item>
        <v-list-item>
          <v-list-item-action @click="handleSelectAllClick">
            <v-checkbox :value="allAdGroupsSelected">
              <template v-slot:label>
                <span class="text-subtitle-2 ml-8 text-black font-weight-bold text--black">
                  Select All
                </span>
              </template>
            </v-checkbox>
          </v-list-item-action>
        </v-list-item>
        <v-divider/>
      </template>
      <template v-slot:item="{ item, attrs: { inputValue, disabled, ripple } }">
        <div style="width: 100%">
          <template>
            <v-list-item class="ma-0 pa-0" :disabled="disabled">
              <v-list-item-action>
                <v-checkbox color="action" :input-value="inputValue" :disabled="disabled" :ripple="ripple"/>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ isDC(item) ? item.party_name : item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template> 
        </div>
      </template>
      <template v-slot:selection="{ item, index }">
        <v-chip v-if="allAdGroupsSelected && index === 1" small>
          <span> All Groups Selected </span>
        </v-chip>
        <v-chip v-else-if="!allAdGroupsSelected && selectedAdGroups.includes(item) && selectedAdGroups.indexOf(item) < 3" small>
          <span> 
            {{ isDC(item) ? item.party_name : item.name }}
          </span> 
        </v-chip>
        <v-chip v-else-if="selectedAdGroups.indexOf(item) === 3 && !allAdGroupsSelected" small>
          <span> {{ `+${selectedAdGroups.length - subtractedHeaders}` }} </span>
        </v-chip>
      </template>
      </v-autocomplete>
    <AdGroupsModal
      v-if="adGroupModal"
      :value="adGroupModal"
      :selectedAdGroups="value"
      @closeDialog="closeAdGroupModal"
    >
    </AdGroupsModal>
  </template>
  <span>Only ad groups associated with the item sources represented by the original promotion are available for selection.</span>
</v-tooltip>
</template>
<script>
// API
import PartyRel from '@/axios/party-relationship-endpoint'
import Items from '@/axios/items-endpoint'
// Components
const AdGroupsModal = () => import('@/components/contracts/AdGroupsModal.vue')
// Mixins
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { displayAlert } from '@/mixins/alert'
export default {
  data() {
    return {
      partiesWithSources: [],
      allAdGroupsSelected: false,
      adGroupModal: false,
      internalUpdate: false,
      initialized: false,
      updatedList: [],
      itemSourceCache: {}
    }
  },
  name: 'CloneModalAdGroups',
  components: { AdGroupsModal },
  mixins: [userAccess, utils, displayAlert],
  props: {
    value: Array,
    adDatesSelected: Boolean,
    existingAdGroups: Array,
    promoItemSources: Array,
    validSources: Array
  },
  async created() {
    const groupIds = [...new Set(this.adGroupSelections.filter(ag => ag.id && ['REGION_GROUP', 'AD_GROUP'].includes(ag.party_type.constant)).flatMap(ag => ag.id))]
    this.partiesWithSources = await this.getPartyItemSources(groupIds)
    this.initialized = true
  },
  watch: {
    value: {
      async handler(newValue, oldValue) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        const oldVal = oldValue ? oldValue : []
        const removedValue = oldValue ? oldVal.filter(o => !newValue.includes(o))[0] : null
        if (this.isDistributionCenterAdded(newValue, oldVal) || this.isRegionGroupAdded(newValue, oldVal)) {
          this.handleParentGroupAdd(newValue, oldVal)
        } else if (this.isAdGroupAdded(newValue, oldVal)) {
          this.handleAddAdGroup(newValue, oldVal)
        } else if (this.isItemRemoved(newValue, oldVal)) {
          this.handleRemoveItem(newValue, oldVal, removedValue)
        }
        
        if (this.allAdGroupsSelected && newValue.length < oldVal.length) {
          this.allAdGroupsSelected = false
        } else if (this.$store.getters.activeDistributionCenters.every(dc => this.updatedList.filter(v => v?.party_type?.constant === "DISTRIBUTION_CENTER").includes(dc))) {
          this.allAdGroupsSelected = true
        }
      }
    }
  },
  computed: {
    activeAdGroups () {
      let groups = this.$store.getters.adGroups.filter((g) => {
        return g?.attributes?.AD_GROUP_IS_ACTIVE === 'True'
          && !this.totalPromoExcludedAdGroups.some(ag => ag.id === g.id)
      })
      if (this.limitAccessByRelatedStores) {
        groups = groups.filter(g => this.userRelatedPartyIds.includes(g.id))
      }
      groups = [{header: "Ad Groups"}, ...groups]
      return this.sortByKey(groups, 'name')
    },
    subtractedHeaders() {
      return this.adGroupSelections.filter(a => a.header != null).length
    },
    excludedAdGroups() {
      return [...this.adPromoOnlyExcludedAdGroups, ...this.totalPromoExcludedAdGroups]
    },
    totalPromoExcludedAdGroups() {
      return this.$store.getters.totalPromoExcludedAdGroups
    },
    adPromoOnlyExcludedAdGroups() {
      return this.$store.getters.adPromoOnlyExcludedAdGroups
    },
    distributionCenters () {
      return[{header: 'Distribution Centers'}, ...this.$store.getters.activeDistributionCenters]
    },
    regionGroups () {
      return[{header: 'Region Groups'}, ...this.$store.getters.activeRegionGroups]
    },
    adGroupSelections () {
      const adGroups = this.tenant === 'alliance-retail-group' ? [...this.distributionCenters, ...this.regionGroups, ...this.activeAdGroups] : [...this.activeAdGroups]
      adGroups.forEach(group => {
        if (this.existingAdGroups.some(eg => eg.store_id === group.id)) {
          group.disabled = true
          return
        }
        if (group?.party_type?.constant === 'AD_GROUP') {
          const partyWithSources = this.partiesWithSources.find(party => party.party_id === group.id)
          if (partyWithSources) {
            let containsSource = false
            this.promoItemSources.forEach(pis => {
              if (partyWithSources?.sources.includes(pis)) {
                containsSource = true
                return
              }
            })
            group.disabled = containsSource ? false : true
          }
        }
        if (['REGION_GROUP', 'DISTRIBUTION_CENTER'].includes(group?.party_type?.constant)) {
          group.member_ids.forEach(mid => {
            const memberWithSources = this.partiesWithSources.find(party => party.party_id === mid)
            if (memberWithSources) {
              let containsSource = false
              this.promoItemSources.forEach(pis => {
                if (memberWithSources?.sources.includes(pis)) {
                  containsSource = true
                  return
                }
              })
              group.disabled = containsSource ? false : true
            }
          })
        }
      })
      return adGroups
    },
    selectedAdGroups() {
      if (!this.initialized) {
        const distributionCenters = this.distributionCenters.filter(dc => dc.member_ids?.every(mi => this.value.includes(mi)))
        const regionGroups = this.regionGroups.filter(rg => rg.member_ids?.every(mi => this.value.includes(mi)))
        const adGroups = this.value.flatMap(v => {
        const group = this.activeAdGroups.find(ag => ag.id === v && (!distributionCenters.some(dc => dc.member_ids.includes(v)) && !regionGroups.some(rg => rg.member_ids.includes(v))))
          return group ? group : []
        })
        return this.sortByItemName([...regionGroups, ...distributionCenters, ...adGroups])
      } else {
        const distributionCenters = this.value.filter(v => v?.party_type?.constant === 'DISTRIBUTION_CENTER')
        const regionGroups = this.value.filter(v => v?.party_type?.constant === 'REGION_GROUP')
        const adGroups = this.value.filter(v => v?.party_type?.constant === 'AD_GROUP' && (!distributionCenters.some(dc => dc.member_ids.includes(v.id)) && !regionGroups.some(rg => rg.member_ids.includes(v.id))))
        return this.sortByItemName([...regionGroups, ...distributionCenters, ...adGroups])
      }
    },
  },
  methods: {
    async getPartyItemSources (partyIds) {
      if (this.validSources.length > 1) {
        try {
          let mapped = []
          const res = await this.getUpstreamPartyRels(partyIds)
          if (res?.data?.length > 0) {
            const upstreamIds = [...new Set(res.data.map(result => result.to_party_id))]
            const results = await this.getUpstreamSourceRels(upstreamIds)
            if (results.length > 0) {
              mapped = partyIds.map(party_id => {
                const upstreamRels = res.data.filter(r => r.from_party_id === party_id)
                const upstreamIds = upstreamRels.map(r => r.to_party_id)
                const sourceRels = results.filter(result => upstreamIds.includes(result.party_id))
                const sources = sourceRels.map(s => s.source_id)
                return { party_id, sources, upstreamIds, upstreamRels, sourceRels }
              })
            }
          }
          return mapped
        } catch (err) {
          this.handleError(err)
        }
      }
    },
    async getUpstreamPartyRels (partyIds) {
      if (partyIds.length === 0) return []
      const payload = {
        from_party_ids: partyIds,
        excluded_upstream_types: ['WHOLESALER', 'REGION_GROUP', 'STORE_GROUP', 'AD_GROUP', 'OWNER']
      }
      return PartyRel.getUpstreamRelationships(payload)
    },
    async getUpstreamSourceRels(upstreamIds) {
      const promises = upstreamIds.map(id => {
        const sourceRels = this.itemSourceCache[id]
        return sourceRels || Items.getSourceRelsByPartyId(id).then(res => {
          this.itemSourceCache[id] = res.data
          return res.data
        })
      })
      return this.getAllSettled(promises)
    },
    handleSelectAllClick() {
      if (!this.allAdGroupsSelected) {
        const allDCs = this.$store.getters.activeDistributionCenters.filter(adc => !adc.disabled)
        const allRGs = this.$store.getters.activeRegionGroups.filter(arg => !arg.disabled)
        const adGroups = this.activeAdGroups.filter(aag => !aag.disabled)
        this.$emit('update:value', [...allRGs, ...allDCs, ...adGroups])
      } else {
        this.$emit('update:value', [])
        this.$emit('update:existingAdGroups', [])
      }
    },
    handleChange(event) {
      this.$emit('update:value', event)
    },
    closeAdGroupModal() {
      this.adGroupModal = false
    },
    isDC(item) {
      return item?.party_type === 'DISTRIBUTION_CENTER'
    },
    isDistributionCenterAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'DISTRIBUTION_CENTER' && newValue.length > oldValue.length
    },
    isRegionGroupAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'REGION_GROUP' && newValue.length > oldValue.length
    },
    isAdGroupAdded(newValue, oldValue) {
      return newValue[newValue.length - 1]?.party_type?.constant === 'AD_GROUP' && newValue.length > oldValue.length
    },
    isItemRemoved(newValue, oldValue) {
      return newValue.length < oldValue.length && newValue.length > 0
    },
    handleAddAdGroup(newValue) {
      const groupToAdd = newValue[newValue.length-1]
      const parentGroups = [...this.regionGroups, ...this.distributionCenters].filter(g => (g?.member_ids?.includes(groupToAdd.id)))
      const groupsToAdd = parentGroups.filter(g => g?.member_ids?.every(mi => this.value.map(v => v.id).includes(mi)))
      if (this.excludedAdGroups.includes(groupToAdd)) {
        this.$emit('update:existingAdGroups', [...this.existingAdGroups, groupToAdd])
      }
      if (groupsToAdd.length > 0) {
        this.updatedList = this.sortByItemName(Array.from(new Set([...groupsToAdd, ...newValue])))
      } else {
        this.updatedList = this.sortByItemName(newValue)
      }
      this.$emit('update:value', this.updatedList)
    },
    handleParentGroupAdd(newValue) {
      let adGroups = newValue[newValue.length - 1].member_ids.map(mi => this.activeAdGroups.find(ag => ag.id === mi && !ag.disabled)).filter(ag => ag)
      const parentGroupsToAdd = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.every(mi => newValue[newValue.length - 1].member_ids.includes(mi))) && !group.disabled).filter(ag => ag)
      this.updatedList = this.sortByItemName(Array.from(new Set([...parentGroupsToAdd, ...adGroups, ...newValue.map(item => {return item})])))
      this.internalUpdate = true
      this.$emit('update:value', this.updatedList)
    },
    handleRemoveItem(newValue, oldValue, removedValue) {
      if (removedValue?.party_type?.constant === 'DISTRIBUTION_CENTER' || removedValue?.party_type?.constant === 'REGION_GROUP') {
        const adGroupsToRemove = removedValue.member_ids.map(mi => this.activeAdGroups.find(ag => ag.id === mi))
        if (adGroupsToRemove.some(ag => this.excludedAdGroups.includes(ag))) {
          this.$emit('update:existingAdGroups', this.existingAdGroups.filter(m => !adGroupsToRemove.includes(m)))
        }
        const parentGroupsToRemove = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.some(mi => removedValue.member_ids.includes(mi))))
        this.updatedList = this.sortByItemName(oldValue.filter(ag => ![removedValue, ...adGroupsToRemove, ...parentGroupsToRemove].includes(ag)))
        this.internalUpdate = true
        this.$emit('update:value', this.updatedList)
      } else {
        const parentGroupsToRemove = [...this.distributionCenters, ...this.regionGroups].filter(group => (group?.member_ids?.includes(removedValue.id)))
        this.updatedList = this.sortByItemName(newValue.filter(value => !parentGroupsToRemove.map(g => g.party_id).includes(value?.id || value)))
        this.internalUpdate = true
        this.$emit('update:value', this.updatedList)
      }
    },
    sortByItemName(array) {
      return array.sort((a,b) => a?.name?.localeCompare(b.name))
    }
},
}
</script>
<style>
div.v-subheader.theme--light {
  font-weight: bold !important;
  color: black !important;
  font-size: large !important;
  margin: 4px 0 0 12px
}
div .v-select__selections {
  padding-top: 8px !important;
}
</style>
